import "./node-pill.css"

import type * as React from "react"

import type { LucideIcon } from "@/components/icons"
import type {
  NodePillConnector,
  NodePillKind,
  NodePillSize,
  NodePillTone,
} from "@/components/ui/node-pill-variants"

import {
  ICON_CONTAINER_SIZE_PX,
  NODE_CONNECTOR_LENGTH_X,
  NODE_PILL_SIZE_CLASS,
  nodePillVariants,
  resolveConnectorSides,
} from "@/components/ui/node-pill-variants"
import { cn } from "@/lib/utils"

export interface NodePillProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  /** `marker` hangs a node circle off the pill, `flow` chains pills, `label` is plain. */
  kind?: NodePillKind
  /** `outlined` = transparent fill; `primary`/`secondary` = flat fill + 1 px outline. */
  tone?: NodePillTone
  /** Font-size step only — every other measure follows X (0.72 em) via CSS. */
  size?: NodePillSize
  /** Which side(s) carry the node circle and the 3X connector line. */
  connector?: NodePillConnector
  /** Flow: highlights the step blue and switches an icon to its solid look. */
  active?: boolean
  /** `true` = ABC Solar Ultra brand tag; `false` = DM Sans functional tag. */
  brand?: boolean
  icon?: LucideIcon
  /**
   * Explicit fill for `tone="secondary"` — any secondary palette colour, e.g.
   * `"hsl(var(--accent))"`. Tailwind classes on `className` work too and win
   * over the tone default through tailwind-merge.
   */
  color?: string
}

export function NodePill({
  active = false,
  brand = false,
  children,
  className,
  color,
  connector = "none",
  icon: Icon,
  kind = "label",
  size = "md",
  style,
  tone = "outlined",
  ...props
}: NodePillProps) {
  const sides = resolveConnectorSides(kind, connector)
  const hasConnector = sides.left.node || sides.right.node
  const flush = hasConnector && !sides.left.line && !sides.right.line

  const pill = (
    <span
      className={cn(nodePillVariants({ active, brand, kind, size, tone }), className)}
      style={color ? { backgroundColor: color, ...style } : style}
      {...props}
    >
      {Icon ? (
        <Icon
          aria-hidden="true"
          className="node-pill__icon"
          fill={active ? "currentColor" : "none"}
        />
      ) : null}
      {children}
    </span>
  )

  if (!hasConnector) {
    return pill
  }

  return (
    <span
      className={cn(
        "node-pill-group",
        NODE_PILL_SIZE_CLASS[size],
        flush && "node-pill-group--flush"
      )}
    >
      {sides.left.node ? <span aria-hidden="true" className="node-pill__node" /> : null}
      {sides.left.line ? <span aria-hidden="true" className="node-pill__line" /> : null}
      {pill}
      {sides.right.line ? <span aria-hidden="true" className="node-pill__line" /> : null}
      {sides.right.node ? <span aria-hidden="true" className="node-pill__node" /> : null}
    </span>
  )
}

export interface NodeConnectorProps extends React.HTMLAttributes<HTMLSpanElement> {
  orientation?: "horizontal" | "vertical"
  /** Line length in X units (X = 0.72 em of the connector's own font-size). */
  length?: number
}

/** A standalone line + node, for joining two containers. */
export function NodeConnector({
  className,
  length = NODE_CONNECTOR_LENGTH_X,
  orientation = "horizontal",
  style,
  ...props
}: NodeConnectorProps) {
  return (
    <span
      aria-hidden="true"
      className={cn("node-connector", `node-connector--${orientation}`, className)}
      style={{ "--node-connector-length": length, ...style } as React.CSSProperties}
      {...props}
    >
      <span className="node-connector__line" />
      <span className="node-connector__node" />
    </span>
  )
}

export interface IconContainerProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Container edge in px. 64 px keeps the brand's 20 px padding / 24 px icon. */
  size?: number
  active?: boolean
}

/** Fully rounded container for a single icon. Filled state keeps the 1 px outline. */
export function IconContainer({
  active = false,
  children,
  className,
  size = ICON_CONTAINER_SIZE_PX,
  style,
  ...props
}: IconContainerProps) {
  return (
    <span
      className={cn("icon-container", active && "icon-container--active", className)}
      style={{ "--icon-container-size": `${size}px`, ...style } as React.CSSProperties}
      {...props}
    >
      {children}
    </span>
  )
}
